import { ref } from 'vue';
import axios from 'axios';
import { useClientLog } from './useClientLog';

const API_PAYMENT_BASE_URL = import.meta.env.VITE_PAYMENT_API_URL;
const API_BASE_URL = import.meta.env.VITE_API_URL;

const QR_REQUEST_TIMEOUT_MS = 15000;

export function usePayment() {
  const imageSrc = ref('');
  const isLoading = ref(true);
  const transactionInfo = ref(null);
  // ทุกเส้นทางที่ทำให้ QR ไม่ขึ้นต้องตั้งค่านี้ ไม่งั้นผู้ใช้จะเจอ spinner ค้าง
  // หรือกล่องเปล่า โดยไม่มีทางไปต่อ ดู docs/donate-qr-image-bug.md
  const qrError = ref('');

  const { reportClientLog, describeAxiosError } = useClientLog();

  const failQRCode = (message, event, details = {}) => {
    qrError.value = message;
    isLoading.value = false;
    reportClientLog(event, details);
  };

  const createQRCode = async (donateInfo, billerId) => {
    const data = {
      qrType: 'PP',
      ppType: 'BILLERID',
      ppId: billerId,
      amount: donateInfo.amount,
      ref1: donateInfo.personal_id.toString(),
      ref2: donateInfo.mobile_number,
      ref3: 'TSU',
    };

    qrError.value = '';
    isLoading.value = true;

    try {
      const response = await axios.post(`${API_PAYMENT_BASE_URL}/payment/qrcode`, data, {
        timeout: QR_REQUEST_TIMEOUT_MS,
      });

      const apiStatusCode = response.data?.status?.code;
      const qrImage = response.data?.data?.qrImage;

      if (response.status === 200 && apiStatusCode === 1000 && qrImage) {
        imageSrc.value = `data:image/png;base64,${qrImage}`;
        isLoading.value = false;
        return;
      }

      // ตอบ 200 แต่เนื้อในไม่ใช่ QR — เดิมตรงนี้ไม่ปิด spinner เลย ผู้ใช้จึงเห็นวงกลมหมุนค้างถาวร
      failQRCode('ไม่สามารถสร้าง QR Code ได้ในขณะนี้', 'qr-bad-status-code', {
        httpStatus: response.status,
        apiStatusCode,
      });
    } catch (error) {
      console.error('Error creating QR code:', error);
      failQRCode('ไม่สามารถเชื่อมต่อระบบชำระเงินได้', 'qr-network-error', describeAxiosError(error));
    }
  };

  const createEdonatQrcode = () => {
    // ต้องมี slash นำหน้า ไม่งั้นบน /donate/:id จะไปหา /donate/images/edonate.png
    // แล้ว nginx try_files จะตอบ index.html กลับมาด้วย HTTP 200 รูปจึงแตกโดยไม่มี 404 ให้เห็น
    imageSrc.value = '/images/edonate.png';
    qrError.value = '';
    isLoading.value = false;
  };

  const getBiller = async (bankType) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/biller`);
      const result = response.data.filter((obj) => obj.BANK_TYPE == bankType);
      return result[0];
    } catch (error) {
      console.error('Error fetching biller:', error);
      return null;
    }
  };

  const POLL_INTERVAL_MS = 5000;
  const POLL_TIMEOUT_MS = 15 * 60 * 1000;

  // Read-only: this only tells the UI when the bank has confirmed. The
  // donation itself is settled server-side by POST /payment/callback.
  const paymentCheck = (checkPayment, callback) => {
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        clearInterval(interval);
        return;
      }
      try {
        const response = await axios.get(`${API_PAYMENT_BASE_URL}/payment/payment-confirm/${checkPayment}`);
        if (response.status === 200) {
          const data = JSON.parse(response.data);
          if (data) {
            transactionInfo.value = data;
            callback(data);
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Payment check error:', error);
      }
    }, POLL_INTERVAL_MS);
    return interval;
  };

  const updateTransaction = async (transactionData) => {
    try {
      await axios.post(`${API_BASE_URL}/update-transction`, transactionData);
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
  };

  const uploadSlip = async (formData, onUploadProgress) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/uploadslip`, formData, {
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading slip:', error);
      throw error;
    }
  };

  return {
    imageSrc,
    isLoading,
    qrError,
    failQRCode,
    transactionInfo,
    createQRCode,
    createEdonatQrcode,
    getBiller,
    paymentCheck,
    updateTransaction,
    uploadSlip,
  };
}
